import { Login, createBaseUserFromApi, } from 'api/model/user';
import { apiFetch } from '@newtontechnologies/beey-api-js-client/apilib';
import { defaultListing, json, hasErrorMessage, } from '@newtontechnologies/beey-api-js-client/receivers';
import { apiV2 } from '@newtontechnologies/beey-api-js-client/endpoints';
import { BeeyLocale } from 'libs/locales/locale';
import { defaultUserSettings } from './settings/user-settings';
export const fetchLoginInformation = async (connection) => connection.fetch()
    .url(apiV2.currentUser.url())
    .receive(json().map(Login.fromAPI))
    .fetch();
export const loginFetch = (onVersionMismatch) => apiFetch()
    .version(VERSION)
    .url(apiV2.currentUser.url())
    .receive(json()
    .map(Login.fromAPI)
    .catchHttpError((response) => {
    switch (response.status) {
        case 401:
            return { error: 'unauthorized' };
        case 403:
            return { error: 'forbidden' };
        case 412:
            return { error: 'precondition-failed' };
        case 422:
            return { error: 'unprocessable-entity' };
        default:
            return 'unknown';
    }
})
    .finally(() => 'unexpected'))
    .tap((response) => {
    if (response.versionMatch !== 'perfect') {
        onVersionMismatch(response.versionMatch);
        throw new Error('Version mismatch');
    }
});
export const loginByResuming = (onVersionMismatch) => loginFetch(onVersionMismatch)
    .url(apiV2.currentUser.url())
    .fetchStrict();
export const loginByCredentials = (email, password, dataProtectionConsent, onVersionMismatch) => loginFetch(onVersionMismatch)
    .postJson({
    Email: email,
    Password: password,
    DataProtectionConsent: dataProtectionConsent,
    UseCookies: true,
})
    .url(apiV2.login.url())
    .fetchStrict();
export const signupByCredentials = async (email, password, uiLocale, userOrigin, userField) => {
    const settings = Object.assign(Object.assign({}, defaultUserSettings), { startBeeyTutorial: true });
    return apiFetch()
        .version(VERSION)
        .url(apiV2.registerAndLogin.url())
        .receive(json()
        .catchHttpError((response, errorPayload) => {
        if (response.status === 422) {
            if (hasErrorMessage(errorPayload, 'Email already used: email')) {
                return { error: 'duplicate-email' };
            }
            if (hasErrorMessage(errorPayload, 'Invalid email: email')) {
                return { error: 'invalid-email' };
            }
            return 'unknown';
        }
        return 'unknown';
    })
        .finally(() => 'unexpected')
        .map(Login.fromAPI))
        .postJson({
        Email: email,
        Password: password,
        Language: uiLocale.code,
        Settings: settings,
        Metadata: [
            { Key: 'userOrigin', Value: userOrigin },
            { Key: 'userField', Value: userField },
        ],
        UseCookies: true,
    })
        .fetchStrict();
};
export const upgradeCurrentUser = (upgradeToken) => apiFetch()
    .url(apiV2.currentUser.activateAccount.url({ upgradeToken }))
    .post()
    .receive(json()
    .map((apiUser) => (Object.assign(Object.assign({}, apiUser), createBaseUserFromApi(apiUser))))
    .catchHttpError((response) => {
    switch (response.status) {
        case 412:
            return { error: 'invalid-token' };
        case 422:
            return { error: 'invalid-data' };
        default:
            return 'unknown';
    }
})
    .finally(() => 'unexpected'))
    .fetchStrict();
export const sendActivationEmail = async (connection) => connection.fetch()
    .url(apiV2.currentUser.sendActivationEmail.url())
    .post()
    .receive(json()
    .finally(() => 'unexpected'))
    .fetchStrict();
export const storeUserUiLocale = (connection, locale) => connection.fetch()
    .url(apiV2.currentUser.setLanguage.url())
    .postJson({ Language: locale.code })
    .receive(json()
    .catchHttpError((response) => (response.status === 422 ? { error: 'invalid-language' } : 'unknown'))
    .finally(() => 'unexpected'))
    .fetchStrict();
export const logoutUser = (connection) => connection.fetch()
    .url(apiV2.logout.url())
    .post()
    .receive(json()
    .catchHttpError((response) => (response.status === 404 ? { error: 'not-logged-in' } : 'unknown'))
    .finally(() => 'unexpected'))
    .fetchStrict();
export const resetPasswordRequest = async (email) => apiFetch()
    .url(apiV2.resetPasswordRequest.url())
    .postJson({ Email: email })
    .receive(json()
    .catchHttpError((response) => (response.status === 422 ? { error: 'invalid-email' } : 'unknown'))
    .finally(() => 'unexpected'))
    .fetchStrict();
export const resetPassword = async (password, resetToken) => apiFetch()
    .url(apiV2.currentUser.resetPassword.url())
    .postJson({
    ResetToken: resetToken,
    Password: password,
})
    .receive(json()
    .catchHttpError((response, errorPayload) => {
    if (response.status === 412)
        return { error: 'invalid-token' };
    if (response.status === 422 && hasErrorMessage(errorPayload, 'Used password'))
        return { error: 'used-password' };
    if (response.status === 422)
        return { error: 'password-too-weak' };
    return 'unknown';
})
    .finally(() => 'unexpected'))
    .fetchStrict();
export const storeUserSettings = async (connection, settings) => connection.fetch()
    .url(apiV2.currentUser.settings.url())
    .postJson(settings)
    .send();
export const fetchStripeCustomerPortal = async (connection, returnUrl, landing, subscriptionKey, subscriptionItemKey, subscribeToPriceId) => connection.fetch()
    .url(apiV2.currentUser.stripeCustomerPortal.url({
    returnUrl, landing, subscriptionKey, subscriptionItemKey, subscribeToPriceId,
}))
    .receive(json({ camelcaseResponse: false }))
    .fetch();
export const fetchCurrentUserTranscriptionReport = async (connection, listing) => connection.fetch()
    .url(apiV2.currentUser.aggregatedTranscriptionLog.url(Object.assign({}, listing !== null && listing !== void 0 ? listing : defaultListing)))
    .receive(json())
    .fetch();
export const fetchTranscriptionLog = async (connection) => connection.fetch()
    .receive(json())
    .url(apiV2.currentUser.transcriptionLog.url())
    .fetch();
export const changeOwnPassword = async (connection, oldPass, newPass) => connection.fetch()
    .url(apiV2.currentUser.changePassword.url())
    .postJson({ Password: oldPass, NewPassword: newPass })
    .receive(json()
    .catchHttpError((response, errorPayload) => {
    if (response.status === 422 && hasErrorMessage(errorPayload, 'Wrong password')) {
        return { error: 'wrong-password' };
    }
    if (response.status === 422 && hasErrorMessage(errorPayload, 'Used password')) {
        return { error: 'used-password' };
    }
    return 'unknown';
})
    .finally(() => 'unexpected'))
    .fetchStrict();
export const fetchPasswordSettings = async () => apiFetch()
    .url(apiV2.passwordSettings.url())
    .receive(json())
    .fetch();
export const extractLanguagesFromProfiles = (profiles) => {
    const languages = new Set();
    profiles.forEach((profile) => {
        const locale = BeeyLocale.fromCode(profile.lang);
        if (locale !== null) {
            languages.add(locale.code);
        }
    });
    return Array.from(languages).flatMap((code) => {
        const locale = BeeyLocale.fromCode(code);
        return locale ? [locale] : [];
    });
};
export const fetchApiToken = (connection) => connection.fetch()
    .url(apiV2.apiToken.url())
    .receive(json()
    .catchHttpError((response) => (response.status === 404 ? { error: 'no-token' } : 'unknown'))
    .finally(() => 'unexpected'))
    .fetchStrict();
export const renewToken = (connection) => connection.fetch()
    .url(apiV2.apiToken.url())
    .postJson({})
    .receive(json()
    .catchHttpError((response) => (response.status === 422 ? { error: 'invalid-api-token' } : 'unknown'))
    .finally(() => 'unexpected'))
    .fetchStrict();
export const fetchInputHistory = async (connection, inputId, skip, count) => {
    const history = await connection.fetch()
        .url(apiV2.currentUser.inputHistory.url({ inputId, skip, count }))
        .receive(json())
        .fetch();
    return history.inputTexts.map((input) => input.text);
};
export const saveInputHistory = async (connection, inputId, text) => connection.fetch()
    .url(apiV2.currentUser.inputHistory.url())
    .postJson({ inputId, text })
    .send();
export const getPurposeFromSource = (source) => {
    switch (source) {
        case 'file':
        case 'link':
            return 'file';
        case 'mic':
            return 'live';
        default:
            return 'file';
    }
};
export const fetchTranscriptionProfiles = async (connection, purpose) => connection.fetch()
    .url(apiV2.currentUser.transcriptionProfiles.url({ purpose }))
    .receive(json()
    .finally(() => 'unexpected'))
    .fetchStrict();
export const validateTranscriptionProfiles = async (connection) => connection.fetch()
    .url(apiV2.currentUser.validateTranscriptionProfiles.url())
    .receive(json())
    .fetch();
export const sendBugReport = async (connection, bugReport) => connection.fetch()
    .url(apiV2.currentUser.bugReport.url())
    .postJson(bugReport)
    .receive(json()
    .catchHttpError((response) => {
    if (response.status === 422) {
        return { error: 'validation-failed' };
    }
    if (response.status === 429) {
        return { error: 'rate-limit-exceeded' };
    }
    return 'unknown';
})
    .finally(() => 'unexpected'))
    .fetchStrict();
